/**
 * React Query keys and functions for blocks data
 *
 * This file centralizes all query keys and API functions for React Query.
 * Used by Content Manager for organizational operations on root blocks.
 */

import type { PlayBlockType, RootBlockDTO } from '@/types/blocks'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'

// ============================================================================
// TYPES
// ============================================================================

export interface BlockQueryParams {
	section?: 'all' | 'starred' | 'trash' | 'recents'
	playbookId?: string
	folderId?: string | null
	blockTypes?: PlayBlockType[]
	limit?: number
	cursor?: string
	teamId?: string | null
}

export interface BlockPage {
	items: RootBlockDTO[]
	nextCursor: string | null
	hasMore: boolean
}

// ============================================================================
// QUERY KEY FACTORIES
// ============================================================================

export const blockKeys = {
	all: ['blocks'] as const,
	lists: () => [...blockKeys.all, 'list'] as const,
	list: (params: BlockQueryParams) => [...blockKeys.lists(), params] as const,
	detail: (id: string) => ['blocks', 'detail', id] as const,
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

async function handleResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		if (response.status === 401) {
			throw new Error('UNAUTHORIZED')
		}
		const error = await response.text()
		throw new Error(error || `HTTP ${response.status}`)
	}
	return response.json()
}

function buildBlockParams(params: BlockQueryParams): URLSearchParams {
	const searchParams = new URLSearchParams()

	if (params.limit) {
		searchParams.set('limit', params.limit.toString())
	}
	if (params.cursor) {
		searchParams.set('cursor', params.cursor)
	}
	if (params.section && params.section !== 'all') {
		searchParams.set('section', params.section)
	}
	if (params.playbookId) {
		searchParams.set('playbookId', params.playbookId.toString())
	}
	if (params.folderId !== null && params.folderId !== undefined) {
		searchParams.set('folderId', params.folderId)
	}
	if (params.blockTypes && params.blockTypes.length > 0) {
		searchParams.set('blockTypes', params.blockTypes.join(','))
	}
	if (params.teamId) {
		searchParams.set('team_id', params.teamId.toString())
	}

	return searchParams
}

export async function fetchBlocksPage(params: BlockQueryParams = {}): Promise<BlockPage> {
	// Use playbook-scoped endpoints when playbookId is provided
	if (params.playbookId) {
		// Route to appropriate playbook endpoint based on section
		if (params.section === 'starred') {
			return fetchPlaybookStarred(params.playbookId)
		}
		if (params.section === 'trash') {
			return fetchPlaybookTrash(params.playbookId)
		}
		if (params.section === 'recents') {
			return fetchPlaybookRecents(params.playbookId, params.limit)
		}
		// For 'all' section or no section, use the contents endpoint
		return fetchPlaybookContents(params.playbookId, params.blockTypes)
	}

	// Fall back to original /api/blocks endpoint for non-playbook queries
	const searchParams = buildBlockParams(params)
	const url = searchParams.toString() ? `/api/blocks?${searchParams}` : '/api/blocks'

	const response = await secureFetch(url)
	const data = await handleResponse<BlockPage>(response)
	return data
}

export async function fetchPlaybookContents(
	playbookId: string,
	blockTypes?: PlayBlockType[]
): Promise<BlockPage> {
	const searchParams = new URLSearchParams()
	if (blockTypes && blockTypes.length > 0) {
		searchParams.set('type', blockTypes.join(','))
	}

	const url = searchParams.toString()
		? `/api/playbooks/${playbookId}/contents?${searchParams}`
		: `/api/playbooks/${playbookId}/contents`

	const response = await secureFetch(url)
	const data = await handleResponse<{ blocks: RootBlockDTO[] }>(response)
	return {
		items: data.blocks,
		nextCursor: null,
		hasMore: false,
	}
}

export async function fetchPlaybookStarred(playbookId: string): Promise<BlockPage> {
	const response = await secureFetch(`/api/playbooks/${playbookId}/starred`)
	const data = await handleResponse<{ blocks: RootBlockDTO[] }>(response)
	return {
		items: data.blocks,
		nextCursor: null,
		hasMore: false,
	}
}

export async function fetchPlaybookTrash(playbookId: string): Promise<BlockPage> {
	const response = await secureFetch(`/api/playbooks/${playbookId}/trash`)
	const data = await handleResponse<{ blocks: RootBlockDTO[] }>(response)
	return {
		items: data.blocks,
		nextCursor: null,
		hasMore: false,
	}
}

export async function fetchPlaybookRecents(
	playbookId: string,
	limit: number = 20
): Promise<BlockPage> {
	const response = await secureFetch(`/api/playbooks/${playbookId}/recents?limit=${limit}`)
	const data = await handleResponse<{ blocks: RootBlockDTO[] }>(response)
	return {
		items: data.blocks,
		nextCursor: null,
		hasMore: false,
	}
}

export async function toggleBlockStar(id: string): Promise<{ isStarred: boolean }> {
	const response = await secureFetch(`/api/blocks/${id}/star`, {
		method: 'PUT',
	})
	return handleResponse<{ isStarred: boolean }>(response)
}

export async function trashBlock(id: string): Promise<void> {
	const response = await secureFetch(`/api/blocks/${id}`, {
		method: 'DELETE',
	})
	if (!response.ok && response.status !== 401) {
		throw new Error(`Failed to trash block: ${response.status}`)
	}
	if (response.status === 401) {
		throw new Error('UNAUTHORIZED')
	}
}

export async function restoreBlock(id: string): Promise<void> {
	const response = await secureFetch(`/api/blocks/${id}/restore`, {
		method: 'PUT',
	})
	if (!response.ok && response.status !== 401) {
		throw new Error(`Failed to restore block: ${response.status}`)
	}
	if (response.status === 401) {
		throw new Error('UNAUTHORIZED')
	}
}

export async function moveBlockToFolder(id: string, folderId: string | null): Promise<void> {
	const response = await secureJsonFetch(`/api/blocks/${id}/folder`, {
		method: 'PUT',
		body: { folderId },
	})
	if (!response.ok && response.status !== 401) {
		throw new Error(`Failed to move block: ${response.status}`)
	}
	if (response.status === 401) {
		throw new Error('UNAUTHORIZED')
	}
}

export async function permanentDeleteBlock(id: string): Promise<void> {
	const response = await secureFetch(`/api/blocks/${id}/permanent`, {
		method: 'DELETE',
	})
	if (!response.ok && response.status !== 401) {
		throw new Error(`Failed to permanently delete block: ${response.status}`)
	}
	if (response.status === 401) {
		throw new Error('UNAUTHORIZED')
	}
}
